/**
 * ConsentComplianceWidget — admin-dashboard widget showing how many users
 * have signed the active data-processing policy.
 *
 * Surfaces:
 *   - Overall sign-rate gauge.
 *   - Per-role breakdown (student / teacher / admin) with quick "remind"
 *     buttons that re-fire the activation email through the existing
 *     send_notification engine.
 *   - "Export CSV" button → streams `/api/admin/consent/records.csv` for
 *     the legal team's audit binder.
 *
 * The widget is designed to drop into a Card grid on any admin dashboard
 * page — pass `tenantId` and it does the rest.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ShieldCheck, Download, Users } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';

export default function ConsentComplianceWidget({ tenantId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['consent-compliance', tenantId],
    queryFn: () => api.get('/admin/consent/compliance', { params: { tenant_id: tenantId } }).then(r => r.data),
    enabled: !!tenantId,
    staleTime: 60_000,
  });

  const overall = data?.overall;

  const downloadCsv = async () => {
    try {
      const r = await api.get('/admin/consent/records.csv', {
        params: { tenant_id: tenantId },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consent_records_${tenantId}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(getErrorMessage(e) || 'CSV export failed');
    }
  };

  return (
    <Card data-testid="consent-compliance-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-fuchsia-600" /> Consent Compliance
        </CardTitle>
        <Button size="sm" variant="outline" onClick={downloadCsv} disabled={isLoading} data-testid="consent-compliance-csv-btn">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading || !overall ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Active policy <span className="font-mono">{data.policy_version}</span></span>
                <span className="text-sm font-semibold" data-testid="consent-compliance-overall">
                  {overall.signed}/{overall.total} <Badge variant="outline" className="ml-1 text-[10px]">{overall.percent}%</Badge>
                </span>
              </div>
              <Progress value={overall.percent} className="h-2" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(data.by_role || []).map(r => (
                <div key={r.role} className="border rounded p-2 text-center" data-testid={`consent-compliance-role-${r.role}`}>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{r.role}</p>
                  <p className="text-lg font-semibold">{r.percent}%</p>
                  <p className="text-xs text-slate-400">{r.signed}/{r.total}</p>
                  {r.non_signed > 0 && (
                    <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 mt-1">
                      {r.non_signed} pending
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">
              <Users className="h-3 w-3 inline mr-0.5" /> {overall.non_signed} users haven&apos;t signed {data.policy_version} yet.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
